import React from "react";
import { CheckCircle2 } from "lucide-react";

const ACCREDITATIONS = [
  {
    code: "RACGP",
    name: "Royal Australian College of General Practitioners",
    tagline: "5th Edition Standards",
    description:
      "Accredited against the RACGP Standards for General Practices (5th edition) — the national benchmark for safe, high-quality general practice care.",
    chips: ["National standards", "Continuous improvement", "Patient safety"],
    logo:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_medical-site-new-1/artifacts/9u35co4p_RACGP_Acronym.png"
  },
  {
    code: "QPA",
    name: "Quality Practice Accreditation",
    tagline: "Accrediting body",
    description:
      "Independently assessed and certified by QPA — an ISQua-accredited organisation and one of Australia's leading accrediting bodies for general practices.",
    chips: ["Independent audit", "ISQua-recognised", "Best-practice care"],
    logo:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_medical-site-new-1/artifacts/yjw3rng9_QPA.png"
  },
  {
    code: "ACRRM",
    name: "Australian College of Rural & Remote Medicine",
    tagline: "Training practice",
    description:
      "Approved to train Specialist Rural Generalist Medical Practitioners on behalf of ACRRM — a mark of our commitment to teaching excellence and rural healthcare.",
    chips: ["Teaching practice", "Rural & regional", "Clinical mentorship"],
    logo:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_medical-site-new-1/artifacts/wti4ibiq_ACCRM.png"
  }
];

export default function Accreditation() {
  return (
    <section id="accreditation" className="relative px-4 md:px-8 pb-4">
      <div className="mx-auto max-w-[1400px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end mb-10">
          <div>
            <span className="eyebrow">Accreditation & Standards</span>
            <h2 className="section-title">A practice you can trust</h2>
          </div>
          <p className="text-[#4a4a63] leading-relaxed lg:max-w-md lg:ml-auto">
            Independently assessed by leading national bodies — a reflection of our commitment to safe, high-quality patient care and continuous improvement.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ACCREDITATIONS.map(({ code, name, tagline, description, chips, logo }) => (
            <div
              key={code}
              className="relative rounded-[28px] bg-white border border-[#eee6f7] shadow-sm p-7 overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#3dc9b3] to-[#1a2464]" />

              <div className="flex items-center justify-between gap-4 mb-6 mt-2 min-h-[64px]">
                <div className="flex-1 flex items-center">
                  <img
                    src={logo}
                    alt={`${code} logo`}
                    className="max-h-14 md:max-h-16 w-auto object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] font-bold tracking-[0.28em] uppercase text-[#4a4a63]">
                    Accredited
                  </div>
                </div>
              </div>

              <div className="text-[#3dc9b3] font-semibold text-xs uppercase tracking-[0.15em] mb-1">
                {tagline}
              </div>
              <div className="text-[#1a2464] font-bold leading-tight mb-3">{name}</div>
              <p className="text-[#4a4a63] text-sm leading-relaxed">{description}</p>

              <div className="mt-5 flex flex-wrap gap-1.5">
                {chips.map((chip) => (
                  <span
                    key={chip}
                    className="inline-flex items-center gap-1 bg-[#3dc9b3]/10 text-[#1a2464] font-semibold text-[11px] px-2.5 py-1 rounded-full"
                  >
                    <CheckCircle2 className="w-3 h-3 text-[#3dc9b3]" />
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
